import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import QRScanModal from '../components/QRScanModal';
import FieldRecordPanel from '../components/FieldRecordPanel';
import PlateConditionsFields, { conditionsPayload, conditionsSummary } from '../components/PlateConditionsFields';
import { PLATE_STATUS_COLORS, SLANT_STATUS_COLORS } from '../constants/statusColors';
import { CHECK_STATUSES, CONTAMINATION_TYPES } from '../constants/plateChecks';
import { shortLocation } from '../components/specimens/StoragePanel';
import Dialog from '../components/ui/Dialog';
import ActionBar from '../components/ui/ActionBar';
import Button from '../components/ui/Button';

/**
 * Lab Mode — the bench workbench.
 *
 * The dominant interaction at a bench is not browsing a table. It is: scan a
 * thing, see the thing, log what you did to it. So the whole page is one
 * always-focused input and one record view.
 *
 * WHY THE INPUT IS ALWAYS FOCUSED
 *
 * A handheld barcode scanner presents to the OS as a keyboard: it types the
 * code and presses Enter. An always-focused text input is therefore the
 * entire integration — no driver, no library, no serial port. Losing that
 * focus is the difference between the scanner working and not, which is why
 * the refocus effect below runs after every state change rather than once on
 * mount.
 *
 * THE STRAIN PANEL IS THE POINT OF MIGRATION 0002
 *
 * Someone about to subculture a plate needs the medium, temperature and pH
 * that line wants, without leaving the screen. Before strain_id existed the
 * best this could show was "what the bank holds of this species", which is
 * not the same question. Target-vs-actual is only expressible now because
 * migration 0006 gave plate_checks somewhere to record the actual.
 */

const RECENT_KEY = 'mb_lab_recent';
// slant_status (0000): what an inspection may find a slant to be.
const SLANT_CHECK_STATUSES = ['healthy', 'contaminated', 'in_refresh', 'discarded'];

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

export default function Lab() {
  const { t } = useTranslation('lab');
  // The storage units' names are the specimens' (one list, utils.STORAGE_UNITS).
  const { t: tSpec } = useTranslation('specimens');
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [record, setRecord] = useState(null);
  const [strain, setStrain] = useState(null);
  const [checks, setChecks] = useState([]);
  // A scanned collection's field record, read through its own routes.
  const [field, setField] = useState(null);
  const [status, setStatus] = useState({ kind: 'idle' });
  const [recent, setRecent] = useState(readRecent);
  const [checkForm, setCheckForm] = useState(null);
  // A slant is inspected as itself (POST /slants/{id}/reviews). "Log a check"
  // on a slant used to open the plate form and post against its parent plate.
  const [slantCheck, setSlantCheck] = useState(null);
  const [slantReviews, setSlantReviews] = useState([]);
  const inputRef = useRef(null);

  // Refocus after every render: a scanner types into whatever has focus, and
  // any click on the page would otherwise silently disable the hardware.
  useEffect(() => {
    if (!checkForm && !slantCheck && !scanning && inputRef.current) inputRef.current.focus();
  });

  const remember = useCallback((entry) => {
    setRecent(prev => {
      const next = [entry, ...prev.filter(r => r.code !== entry.code)].slice(0, 8);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* fine */ }
      return next;
    });
  }, []);

  const lookup = useCallback(async (raw) => {
    const value = (raw || '').trim();
    if (!value) return;
    setStatus({ kind: 'loading' });
    setRecord(null); setStrain(null); setChecks([]); setField(null);
    try {
      // One lookup for every label in the lab (routes/scan_station.py): what
      // /lookup found, plus instruments, experiments and their units, and
      // culture vessels. `details` is the record itself; for the kinds
      // /lookup knew it is exactly /lookup's answer.
      const r = await api.get(`/scan/${encodeURIComponent(value)}`);
      const found = r.data || {};
      if (!found.kind) {
        setStatus({ kind: 'notFound', code: value });
        return;
      }
      const data = { ...(found.details || {}), type: found.kind, id: found.id,
        code: found.code || found.details?.code, status: found.status ?? found.details?.status,
        title: found.title };
      setRecord(data);
      setStatus({ kind: 'idle' });
      remember({ code: value, type: data.type });

      setSlantReviews([]);
      if (data.type === 'slant' && data.id) {
        api.get(`/slants/${data.id}/reviews`)
          .then(res => setSlantReviews(Array.isArray(res.data) ? res.data : []))
          .catch(() => setSlantReviews([]));
      }
      if (data.type === 'plate' && data.plate_id) {
        api.get(`/plates/${data.plate_id}/checks`)
          .then(res => setChecks(res.data || []))
          .catch(() => setChecks([]));
      }
      if (data.type === 'collection' && data.id) {
        setField({ loading: true });
        const id = encodeURIComponent(data.id);
        Promise.allSettled([
          api.get(`/collections/${id}`),
          api.get(`/collections/${id}/photos`),
          api.get(`/collections/${id}/vegetation`),
        ]).then(([detail, photos, vegetation]) => {
          if (detail.status !== 'fulfilled') {
            // 404 for a student on someone else's specimen: the label is all
            // this person may see, and the panel says so.
            setField({ restricted: true });
            return;
          }
          setField({
            detail: detail.value.data,
            photos: photos.status === 'fulfilled' && Array.isArray(photos.value.data) ? photos.value.data : [],
            vegetation: vegetation.status === 'fulfilled' && Array.isArray(vegetation.value.data) ? vegetation.value.data : [],
          });
        });
      }
      if (data.strain_id) {
        api.get(`/strains/${data.strain_id}`)
          .then(res => setStrain(res.data))
          .catch(() => setStrain(null));
      }
    } catch (e) {
      if (e?.response?.status === 404) setStatus({ kind: 'notFound', code: value });
      else setStatus({ kind: 'failed' });
    }
  }, [remember]);

  const submitSlantCheck = async () => {
    if (record?.type !== 'slant' || !record.id) return;
    setSlantCheck(f => ({ ...f, saving: true, error: '' }));
    try {
      await api.post(`/slants/${record.id}/reviews`, {
        status_at_review: slantCheck.status,
        action_taken: slantCheck.action || undefined,
        notes: slantCheck.notes || undefined,
      });
      setSlantCheck(null);
      lookup(code);
    } catch {
      setSlantCheck(f => ({ ...f, saving: false, error: t('check.failed') }));
    }
  };

  const submitCheck = async () => {
    if (!record?.plate_id) return;
    setCheckForm(f => ({ ...f, saving: true }));
    try {
      await api.post(`/plates/${record.plate_id}/checks`, {
        status: checkForm.status,
        colony_diameter_mm: checkForm.diameter || undefined,
        ph: checkForm.ph || undefined,
        humidity_pct: checkForm.humidity || undefined,
        contamination_type: checkForm.status === 'contaminated'
          ? checkForm.contamination : undefined,
        transfer_to: checkForm.status === 'ready_to_transfer'
          ? checkForm.transferTo : undefined,
        notes: checkForm.notes || undefined,
        ...conditionsPayload(checkForm),
      });
      setCheckForm(null);
      lookup(code);
    } catch {
      setCheckForm(f => ({ ...f, saving: false, error: t('check.failed') }));
    }
  };

  const statusColour = (kind, value) =>
    (kind === 'slant' ? SLANT_STATUS_COLORS : PLATE_STATUS_COLORS)[value] || 'var(--c-text-muted-2)';

  return (
    <div style={styles.page}>
      <section style={styles.scanBox}>
        <h1 style={styles.h1}>{t('scan.title')}</h1>
        <form
          style={styles.scanRow}
          onSubmit={e => { e.preventDefault(); lookup(code); }}
        >
          <input
            ref={inputRef}
            style={styles.scanInput}
            placeholder={t('scan.placeholder')}
            value={code}
            onChange={e => setCode(e.target.value)}
            title={t('tip.scanInput')}
            autoFocus
          />
          <button type="submit" style={styles.primary} title={t('tip.lookUp')}>{t('scan.submit')}</button>
          <button
            type="button"
            style={styles.secondary}
            onClick={() => setScanning(true)}
            title={t('tip.camera')}
          >
            {t('scan.scanBtn')}
          </button>
        </form>
        <p style={styles.hint}>{t('scan.hint')}</p>

        {status.kind === 'notFound' && (
          <p style={styles.warn}>{t('scan.notFound', { code: status.code })}</p>
        )}
        {status.kind === 'failed' && <p style={styles.warn}>{t('scan.failed')}</p>}

        {recent.length > 0 && !record && (
          <div style={styles.recent}>
            <span style={styles.recentLabel}>{t('scan.recent')}</span>
            {recent.map(r => (
              <button
                key={r.code}
                type="button"
                style={styles.chip}
                onClick={() => { setCode(r.code); lookup(r.code); }}
                title={t('tip.recent')}
              >
                {r.code}
              </button>
            ))}
          </div>
        )}
      </section>

      {record && record.type === 'collection' && (
        <div style={styles.grid}>
          <FieldRecordPanel code={record.code || code} title={record.title} field={field} t={t} />
        </div>
      )}

      {record && record.type !== 'collection' && (
        <div style={styles.grid}>
          <section style={styles.card}>
            <h2 style={styles.h2}>
              {t(`record.${record.type}`, { defaultValue: record.type })}
              {' '}
              <code style={styles.code}>{record.code || code}</code>
            </h2>
            {record.title && record.title !== record.code && (
              <p style={{ margin: '0 0 8px' }} data-testid="record-title">{record.title}</p>
            )}
            <dl style={styles.dl}>
              {record.status && (
                <>
                  <dt style={styles.dt}>{t('record.status')}</dt>
                  <dd style={styles.dd}>
                    <span style={{
                      ...styles.pill,
                      background: statusColour(record.type, record.status),
                    }}>
                      {record.status.replace(/_/g, ' ')}
                    </span>
                  </dd>
                </>
              )}
              {record.protocol && (
                <>
                  <dt style={styles.dt}>{t('record.medium')}</dt>
                  <dd style={styles.dd}>{record.protocol}</dd>
                </>
              )}
              {record.temperature_c != null && (
                <>
                  <dt style={styles.dt}>{t('record.temperature')}</dt>
                  <dd style={styles.dd}>{record.temperature_c} °C</dd>
                </>
              )}
              {record.passage_number != null && (
                <>
                  <dt style={styles.dt}>{t('record.passage')}</dt>
                  <dd style={styles.dd}>{record.passage_number}</dd>
                </>
              )}
              {/* 0050, for a slant or an extract this person may see: the
                  specimen it descends from, where its tube is, and when it
                  was banked as what. */}
              {record.specimen_code && (
                <>
                  <dt style={styles.dt}>{t('record.isolateId')}</dt>
                  <dd style={styles.dd}><bdi dir="ltr" data-testid="lab-specimen-code">{record.specimen_code}</bdi></dd>
                </>
              )}
              {record.storage_unit && (
                <>
                  <dt style={styles.dt}>{t('record.storage')}</dt>
                  <dd style={styles.dd}>
                    <bdi dir="ltr" data-testid="lab-storage-location">{shortLocation(record, tSpec)}</bdi>
                  </dd>
                </>
              )}
              {(record.preserved_at || record.preserved_organism) && (
                <>
                  <dt style={styles.dt}>{t('record.preserved')}</dt>
                  <dd style={styles.dd} data-testid="lab-preserved">
                    {record.preserved_at}
                    {record.preserved_at && record.preserved_organism ? ' · ' : ''}
                    {record.preserved_organism && <bdi dir="ltr">{record.preserved_organism}</bdi>}
                  </dd>
                </>
              )}
              <dt style={styles.dt}>{t('record.lastCheck')}</dt>
              <dd style={styles.dd}>{record.last_check_date || t('record.never')}</dd>
            </dl>

            {record.type === 'slant' ? (
              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.primary}
                  data-testid="lab-slant-check"
                  onClick={() => setSlantCheck({ status: record.status || 'healthy' })}
                  title={t('tip.logSlantCheck')}
                >
                  {t('actions.logCheck')}
                </button>
              </div>
            ) : record.plate_id && (
              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.primary}
                  onClick={() => setCheckForm({ status: 'pending', transferTo: 'plate' })}
                  title={t('tip.logCheck')}
                >
                  {t('actions.logCheck')}
                </button>
              </div>
            )}
            {record.type === 'slant' && slantReviews.length > 0 && (
              <ul data-testid="lab-slant-reviews" style={{ margin: '8px 0 0', paddingInlineStart: 18, fontSize: 13 }}>
                {slantReviews.slice(0, 3).map(r => (
                  <li key={r.review_id}>
                    {String(r.review_date || '').slice(0, 10)} · {r.status_at_review || '—'}
                    {r.action_taken ? ` · ${r.action_taken}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section style={styles.card}>
            <h2 style={styles.h2}>{t('strain.title')}</h2>
            {!strain && <p style={styles.muted}>{t('strain.none')}</p>}
            {strain && (
              <>
                <p style={styles.strainName}>
                  <strong>{strain.isolate_code}</strong>
                  {strain.species ? ` — ${strain.species}` : ''}
                </p>
                <h3 style={styles.h3}>{t('strain.requirements')}</h3>
                {(!strain.growth_requirements || strain.growth_requirements.length === 0) && (
                  <p style={styles.muted}>{t('strain.noRequirements')}</p>
                )}
                {(strain.growth_requirements || []).map(g => (
                  <div key={g.growth_stage} style={styles.req}>
                    <strong style={styles.reqStage}>{g.growth_stage}</strong>
                    <Band label={t('strain.temp')} min={g.temp_min} opt={g.temp_opt} max={g.temp_max} unit="°C" />
                    <Band label={t('strain.ph')} min={g.ph_min} opt={g.ph_opt} max={g.ph_max} unit="" />
                    <Band label={t('strain.humidity')} min={g.humidity_min} max={g.humidity_max} unit="%" />
                    {g.substrate_type && (
                      <span style={styles.reqLine}>
                        {t('strain.substrate')}: {g.substrate_type}
                      </span>
                    )}
                  </div>
                ))}
              </>
            )}
          </section>

          {checks.length > 0 && (
            <section style={{ ...styles.card, gridColumn: '1 / -1' }}>
              <h2 style={styles.h2}>{t('history.title')}</h2>
              <ul style={styles.checkList}>
                {checks.map(c => (
                  <li key={c.check_id} style={styles.checkItem}>
                    <span style={{
                      ...styles.pill, background: statusColour('plate', c.status),
                    }}>
                      {(c.status || '').replace(/_/g, ' ')}
                    </span>
                    <span style={styles.checkDate}>{c.check_date}</span>
                    {c.colony_diameter_mm != null && (
                      <span style={styles.checkMeta}>{c.colony_diameter_mm} mm</span>
                    )}
                    {c.ph != null && <span style={styles.checkMeta}>pH {c.ph}</span>}
                    {c.humidity_pct != null && (
                      <span style={styles.checkMeta}>{c.humidity_pct}%</span>
                    )}
                    {conditionsSummary(c, t) && (
                      <span style={styles.checkMeta} data-testid="check-conditions">{conditionsSummary(c, t)}</span>
                    )}
                    {c.notes && <span style={styles.checkNotes}>{c.notes}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {slantCheck && (
        <Dialog title={t('slantCheck.title', { code: record?.code || '' })} titleStyle={styles.h2}
          onClose={() => setSlantCheck(null)} busy={!!slantCheck.saving} dirty testId="lab-slant-check-form">
          <div style={styles.dialogBody}>
            <label style={styles.field}>
              <span style={styles.label}>{t('check.status')}</span>
              <select style={styles.input} value={slantCheck.status} title={t('tip.slantStatus')}
                      onChange={e => setSlantCheck(f => ({ ...f, status: e.target.value }))}>
                {SLANT_CHECK_STATUSES.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>
            <label style={styles.field}>
              <span style={styles.label}>{t('slantCheck.action')}</span>
              <input style={styles.input} value={slantCheck.action || ''} maxLength={128}
                     title={t('tip.slantAction')}
                     onChange={e => setSlantCheck(f => ({ ...f, action: e.target.value }))} />
            </label>
            <label style={styles.field}>
              <span style={styles.label}>{t('check.notes')}</span>
              <textarea style={styles.input} rows={2} value={slantCheck.notes || ''} title={t('tip.slantNotes')}
                        onChange={e => setSlantCheck(f => ({ ...f, notes: e.target.value }))} />
            </label>
            {slantCheck.error && <p style={styles.warn}>{slantCheck.error}</p>}
          </div>
          <ActionBar>
            <Button onClick={() => setSlantCheck(null)} disabled={slantCheck.saving} title={t('tip.cancel')}>
              {t('common:cancel')}
            </Button>
            <Button variant="primary" disabled={slantCheck.saving} onClick={submitSlantCheck}
                    data-testid="lab-slant-check-save" title={t('tip.saveSlantCheck')}>
              {slantCheck.saving ? t('check.saving') : t('check.save')}
            </Button>
          </ActionBar>
        </Dialog>
      )}

      {checkForm && (
        <Dialog title={t('check.title')} titleStyle={styles.h2}
          onClose={() => setCheckForm(null)} busy={!!checkForm.saving} dirty testId="lab-check-form">
          <div style={styles.dialogBody}>
            <label style={styles.field}>
              <span style={styles.label}>{t('check.status')}</span>
              <select
                style={styles.input}
                value={checkForm.status}
                onChange={e => setCheckForm(f => ({ ...f, status: e.target.value }))}
                title={t('tip.status')}
              >
                {CHECK_STATUSES.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </label>

            {checkForm.status === 'contaminated' && (
              <label style={styles.field}>
                <span style={styles.label}>{t('check.contamination')}</span>
                <select
                  style={styles.input}
                  value={checkForm.contamination || ''}
                  onChange={e => setCheckForm(f => ({ ...f, contamination: e.target.value }))}
                  title={t('tip.contamination')}
                >
                  <option value="">—</option>
                  {CONTAMINATION_TYPES.map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </label>
            )}

            {checkForm.status === 'ready_to_transfer' && (
              <label style={styles.field}>
                <span style={styles.label}>{t('check.transferTo')}</span>
                <select
                  style={styles.input}
                  value={checkForm.transferTo}
                  onChange={e => setCheckForm(f => ({ ...f, transferTo: e.target.value }))}
                  title={t('tip.transferTo')}
                >
                  <option value="plate">{t('check.plate')}</option>
                  <option value="slant">{t('check.slant')}</option>
                </select>
              </label>
            )}

            <div style={styles.row3}>
              <label style={styles.field}>
                <span style={styles.label}>{t('check.diameter')}</span>
                <input
                  type="number" style={styles.input}
                  value={checkForm.diameter || ''}
                  onChange={e => setCheckForm(f => ({ ...f, diameter: e.target.value }))}
                  title={t('tip.diameter')}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>{t('check.ph')}</span>
                <input
                  type="number" step="0.1" min="0" max="14" style={styles.input}
                  value={checkForm.ph || ''}
                  onChange={e => setCheckForm(f => ({ ...f, ph: e.target.value }))}
                  title={t('tip.ph')}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>{t('check.humidity')}</span>
                <input
                  type="number" min="0" max="100" style={styles.input}
                  value={checkForm.humidity || ''}
                  onChange={e => setCheckForm(f => ({ ...f, humidity: e.target.value }))}
                  title={t('tip.humidity')}
                />
              </label>
            </div>

            <div style={styles.row3}>
              <PlateConditionsFields values={checkForm} onChange={setCheckForm}
                labelStyle={styles.field} inputStyle={styles.input} />
            </div>

            <label style={styles.field}>
              <span style={styles.label}>{t('check.notes')}</span>
              <textarea
                rows={3} style={{ ...styles.input, resize: 'vertical' }}
                value={checkForm.notes || ''}
                onChange={e => setCheckForm(f => ({ ...f, notes: e.target.value }))}
                title={t('tip.notes')}
              />
            </label>

            {checkForm.error && <p style={styles.warn}>{checkForm.error}</p>}
          </div>
          <ActionBar>
            <Button onClick={() => setCheckForm(null)} disabled={checkForm.saving} title={t('tip.cancel')}>
              {t('common:cancel')}
            </Button>
            <Button variant="primary" disabled={checkForm.saving} onClick={submitCheck}
                    data-testid="lab-check-save" title={t('tip.saveCheck')}>
              {checkForm.saving ? t('check.saving') : t('check.save')}
            </Button>
          </ActionBar>
        </Dialog>
      )}

      <QRScanModal
        open={scanning}
        onClose={() => setScanning(false)}
        onDetected={(scanned) => {
          setScanning(false);
          setCode(scanned);
          lookup(scanned);
        }}
      />
    </div>
  );
}

/** `min – (opt) – max`, or nothing at all when the band was never recorded. */
function Band({ label, min, opt, max, unit }) {
  if (min == null && opt == null && max == null) return null;
  const parts = [];
  if (min != null) parts.push(min);
  if (opt != null) parts.push(`(${opt})`);
  if (max != null) parts.push(max);
  return (
    <span style={styles.reqLine}>
      {label}: {parts.join(' – ')}{unit ? ` ${unit}` : ''}
    </span>
  );
}

const styles = {
  page: { padding: 20, maxWidth: 1100, margin: '0 auto' },
  scanBox: {
    background: 'var(--c-surface)', border: '1px solid var(--c-border-2)', borderRadius: 12,
    padding: 20, marginBottom: 20,
  },
  h1: { margin: '0 0 14px', fontSize: 20, color: 'var(--c-text-2-fg)' },
  h2: { margin: '0 0 12px', fontSize: 17, color: 'var(--c-text-2-fg)' },
  h3: { margin: '14px 0 8px', fontSize: 14, color: 'var(--c-text-body-2-fg)' },
  scanRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  scanInput: {
    flex: '1 1 260px', fontSize: 18, padding: '12px 14px',
    border: '2px solid var(--c-info-2)', borderRadius: 8, font: 'inherit',
  },
  primary: {
    minHeight: 48, padding: '0 22px', border: 'none', borderRadius: 8,
    background: 'var(--c-info-2)', color: 'var(--c-on-accent)', font: 'inherit', fontSize: 16,
    fontWeight: 600, cursor: 'pointer',
  },
  secondary: {
    minHeight: 48, padding: '0 18px', border: '1px solid var(--c-border-strong-2)',
    borderRadius: 8, background: 'var(--c-surface)', font: 'inherit', fontSize: 15,
    cursor: 'pointer',
  },
  hint: { margin: '10px 0 0', fontSize: 13, color: 'var(--c-text-muted-2-fg)' },
  warn: {
    marginTop: 12, padding: '10px 12px', borderRadius: 8,
    background: 'var(--c-danger-soft-2)', color: 'var(--c-danger-4-fg)', fontSize: 14,
  },
  recent: { marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  recentLabel: { fontSize: 12, color: 'var(--c-text-muted-2-fg)', textTransform: 'uppercase' },
  chip: {
    padding: '6px 12px', border: '1px solid var(--c-border-strong-2)', borderRadius: 999,
    background: 'var(--c-surface)', font: 'inherit', fontSize: 13, cursor: 'pointer',
  },
  grid: {
    display: 'grid', gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  },
  card: {
    background: 'var(--c-surface)', border: '1px solid var(--c-border-2)', borderRadius: 12, padding: 18,
  },
  code: { fontFamily: 'monospace', fontSize: 16, color: 'var(--c-info-2-fg)' },
  dl: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', margin: 0 },
  dt: { fontSize: 13, color: 'var(--c-text-muted-2-fg)', fontWeight: 600 },
  dd: { margin: 0, fontSize: 15, color: 'var(--c-text-2-fg)' },
  pill: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 999,
    color: 'var(--c-on-accent)', fontSize: 12, fontWeight: 600,
  },
  actions: { marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' },
  muted: { color: 'var(--c-text-muted-2-fg)', fontSize: 14, margin: 0 },
  strainName: { margin: '0 0 4px', fontSize: 15 },
  req: {
    display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px',
    background: 'var(--c-bg-subtle-5)', borderRadius: 8, marginBottom: 8,
  },
  reqStage: { fontSize: 13, textTransform: 'uppercase', color: 'var(--c-info-2-fg)' },
  reqLine: { fontSize: 13, color: 'var(--c-text-body-2-fg)' },
  checkList: { listStyle: 'none', margin: 0, padding: 0 },
  checkItem: {
    display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
    padding: '10px 0', borderBottom: '1px solid var(--c-bg-subtle-3)', fontSize: 14,
  },
  checkDate: { color: 'var(--c-text-muted-2-fg)', fontSize: 13 },
  checkMeta: { color: 'var(--c-text-body-2-fg)', fontSize: 13 },
  checkNotes: { color: 'var(--c-text-muted-3-fg)', fontSize: 13, flex: 1 },
  // The check dialogs are components/ui/Dialog (2026-10-18); their own
  // overlay sat at z-index 50, under the nav bar.
  dialogBody: { display: 'flex', flexDirection: 'column', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-2-fg)' },
  input: {
    fontSize: 16, padding: '10px 12px', border: '1px solid var(--c-border-strong-2)',
    borderRadius: 8, font: 'inherit', background: 'var(--c-surface)',
  },
  row3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 },
};
